/**
 * Exact decimal math for token amounts ($TRUTH has 8 decimals).
 *
 * Why this exists: every amount the UI shows, validates and submits has to agree
 * with the server's own re-calculation down to the last unit, and the server's
 * balances are plain JS floats (sums of products, so `unlockedRewards` is
 * routinely something like `162.99999999999997`). Doing the arithmetic in floats
 * produces amounts that are either wrong by dust or — worse — *larger* than the
 * balance they were derived from:
 *
 *   (162.99999999999997).toFixed(2)        === "163.00"   // > the balance
 *   162.99999999999997 * 0.5               === 81.49999999999999  // 14 decimals
 *   8.29 * 1e8                             === 828999999.9999999  // floor loses 1 unit
 *
 * The first one is what made the modal reject its own "Max" button, and the
 * second one is what made an amount fail the "at most 8 decimal places" check.
 *
 * The fix is the standard one: parse once into BigInt base units, do all
 * arithmetic there, and format back to a decimal string only for display.
 */

export const TRUTH_DECIMALS = 8;

const pow10 = (exponent: number): bigint => 10n ** BigInt(exponent);

/**
 * Plain (non-exponential) decimal text for a value, precise enough to be
 * truncated at 8 decimals. Returns null for anything that is not a finite,
 * non-negative decimal number.
 */
function toPlainDecimalText(value: number | string): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();

    // Digits with at most one dot. Must contain at least one digit, so "" and
    // "." are rejected rather than parsed as 0.
    return /^\d*\.?\d*$/.test(trimmed) && /\d/.test(trimmed) ? trimmed : null;
  }

  // 1e21 and above is where toFixed switches to exponential notation.
  if (!Number.isFinite(value) || value < 0 || value >= 1e21) return null;

  // 20 fraction digits is well past double precision, so nothing meaningful is
  // lost, and the result is always plain notation for the range above.
  return value.toFixed(20);
}

/**
 * Value to base units, truncated toward zero.
 *
 * Use this for anything derived from a balance: truncation can never produce an
 * amount above the balance, so the server's `amount > available` check cannot
 * fire on a value this UI itself computed.
 */
export function toBaseUnitsFloor(
  value: number | string,
  decimals: number = TRUTH_DECIMALS,
): bigint | null {
  const text = toPlainDecimalText(value);

  if (text === null) return null;

  const [whole, fraction = ""] = text.split(".");
  const kept = fraction.padEnd(decimals, "0").slice(0, decimals);

  return BigInt(whole || "0") * pow10(decimals) + BigInt(kept || "0");
}

/** Value to base units, rounded half-up at the last representable decimal. */
export function toBaseUnitsRound(
  value: number | string,
  decimals: number = TRUTH_DECIMALS,
): bigint | null {
  const text = toPlainDecimalText(value);

  if (text === null) return null;

  const [whole, fraction = ""] = text.split(".");
  const kept = fraction.padEnd(decimals + 1, "0").slice(0, decimals + 1);
  const units = BigInt(whole || "0") * pow10(decimals) + BigInt(kept.slice(0, decimals) || "0");

  return Number(kept[decimals]) >= 5 ? units + 1n : units;
}

/**
 * Strict parse of user-entered text. Returns null when the text is not a decimal
 * number or carries more decimal places than the token can represent (i.e. the
 * amount is not payable), so callers never silently round a user's input.
 */
export function parseExactBaseUnits(
  text: string,
  decimals: number = TRUTH_DECIMALS,
): bigint | null {
  const trimmed = (text || "").trim();

  if (!/^\d*\.?\d*$/.test(trimmed) || !/\d/.test(trimmed)) return null;

  const [, fraction = ""] = trimmed.split(".");

  if (fraction.length > decimals) return null;

  return toBaseUnitsFloor(trimmed, decimals);
}

/** Base units back to decimal text, trailing zeros trimmed ("81.50000000" -> "81.5"). */
export function formatBaseUnits(units: bigint, decimals: number = TRUTH_DECIMALS): string {
  const isNegative = units < 0n;
  const absolute = isNegative ? -units : units;
  const scale = pow10(decimals);
  const whole = (absolute / scale).toString();
  const fraction = (absolute % scale).toString().padStart(decimals, "0").replace(/0+$/, "");

  return `${isNegative ? "-" : ""}${whole}${fraction ? `.${fraction}` : ""}`;
}

/**
 * Base units as a grouped display string. Truncates (never rounds up) at
 * `maxFractionDigits` so a headline balance can never claim more is available
 * than actually is.
 */
export function formatBaseUnitsForDisplay(
  units: bigint,
  {
    decimals = TRUTH_DECIMALS,
    maxFractionDigits = TRUTH_DECIMALS,
  }: { decimals?: number; maxFractionDigits?: number } = {},
): string {
  const truncated =
    maxFractionDigits >= decimals
      ? units
      : (units / pow10(decimals - maxFractionDigits)) * pow10(decimals - maxFractionDigits);

  // Display only, so the Number() hop is fine: it would lose precision above
  // ~9e7 $TRUTH, which no balance reaches. Never feed this back into arithmetic.
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: maxFractionDigits,
  }).format(Number(formatBaseUnits(truncated, decimals)));
}

/**
 * A claimable balance as display text.
 *
 * Truncated, never rounded up: `toFormattedDigits` renders a balance of
 * 162.99999999999997 as "163", so the UI advertised more than the claim form
 * would accept. Use this wherever the number shown is something the user is
 * about to claim.
 */
export function formatClaimableAmount(
  value: number | string | undefined | null,
  maxFractionDigits: number = 2,
): string {
  const units = toBaseUnitsFloor(value ?? 0) ?? 0n;

  return formatBaseUnitsForDisplay(units, { maxFractionDigits });
}

/**
 * `units * numerator / denominator` in base units.
 *
 * "round" is right for a fraction of a balance (50% of 162.99999999 should read
 * as 81.5, not 81.49999999); "floor" is right when the result must stay at or
 * below the input.
 */
export function scaleBaseUnits(
  units: bigint,
  numerator: bigint,
  denominator: bigint,
  rounding: "floor" | "round" = "floor",
): bigint {
  const product = units * numerator;
  const quotient = product / denominator;

  if (rounding === "round" && (product % denominator) * 2n >= denominator) {
    return quotient + 1n;
  }

  return quotient;
}

/**
 * Decimal places in a value's shortest decimal representation, or null when it
 * is not a finite plain number.
 *
 * Use this instead of the `Math.abs(Math.round(x * 1e8) - x * 1e8) < 1e-9`
 * pattern: at a magnitude of 1e9-1e10 a double's own spacing is larger than the
 * 1e-9 tolerance, so that comparison rejects roughly one in seven legitimate
 * 8-decimal amounts (19.00071271 * 1e8 lands on 1900071270.9999998).
 */
export function decimalPlaces(value: number | string): number | null {
  const text =
    typeof value === "number"
      ? Number.isFinite(value)
        ? String(value)
        : null
      : (value || "").trim();

  if (text === null) return null;

  const match = /^-?(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/.exec(text);

  if (!match) return null;

  const fractionLength = (match[2] ?? "").length;
  const exponent = match[3] ? parseInt(match[3], 10) : 0;

  return Math.max(0, fractionLength - exponent);
}

/**
 * Normalise what a user typed into a decimal field: digits, at most one dot, at
 * most `decimals` fraction digits. Keeps partial input like "81." usable while
 * making it impossible to enter an unpayable amount in the first place.
 */
export function sanitizeDecimalInput(text: string, decimals: number = TRUTH_DECIMALS): string {
  const digitsAndDots = (text || "").replace(/[^\d.]/g, "");
  const [whole = "", ...rest] = digitsAndDots.split(".");
  const hasDot = rest.length > 0;
  const fraction = rest.join("").slice(0, decimals);
  const normalisedWhole = whole.replace(/^0+(?=\d)/, "");

  if (!hasDot) return normalisedWhole;

  return `${normalisedWhole || "0"}.${fraction}`;
}

export default {
  TRUTH_DECIMALS,
  decimalPlaces,
  formatBaseUnits,
  formatBaseUnitsForDisplay,
  formatClaimableAmount,
  parseExactBaseUnits,
  sanitizeDecimalInput,
  scaleBaseUnits,
  toBaseUnitsFloor,
  toBaseUnitsRound,
};
